
    import { Component, Vue, Watch } from 'vue-property-decorator';
    import { mapActions, mapState } from 'vuex';
    import Kartei from '@/components/Kartei.vue';
    import ImpferinnerungsbriefDetail from '@/components/Impferinnerungen/ImpferinnerungsbriefDetail.vue';
    import Impferinnerung from '@/types/Impferinnerung';
    import Impfung from '@/types/Impfung';
    import { ImpferinnerungenFilter, ImpferinnerungenPagination, ValidationRules } from '@/store/types';
    import {
        AlsErinnertMarkierenPayload,
        LoadImpferinnerungenPayload,
        LoadKarteiImpfungenPayload, PrintBriefePayload, SendEmailPayload,
    } from '@/store/impferinnerungen';
    import { CompareFunction, DataTableHeaders } from '@/types';
    import { confirmSwal } from '@/helper/swals';
    import { SweetAlertResult } from 'sweetalert2';

    @Component({
        components: {
            Kartei,
            ImpferinnerungsbriefDetail,
        },
        computed: {
            ...mapState('globals', ['formatDate', 'parseDate', 'rules', 'sortDate']),
            ...mapState('impferinnerungen', [
                'kartei',
                'impferinnerungen',
                'selectedItems',
                'filter',
                'loaders',
                'dialogs',
                'brief',
                'totalCount',
            ]),
        },
        methods: {
            ...mapActions('impferinnerungen', [
                'loadImpferinnerungen',
                'loadKarteiImpfungen',
                'setSelectedItems',
                'setBrief',
                'setDialogs',
                'openKartei',
                'openBrief',
                'printBriefe',
                'sendEmail',
                'alsErinnertMarkieren',
            ]),
        },
    })
    export default class Impferinnerungen extends Vue {
        public impferinnerungen!: Impferinnerung[];
        public selectedItems!: Impferinnerung[];
        public rules!: ValidationRules;
        public filter!: ImpferinnerungenFilter;
        public parseDate!: (input: string | null) => string | null;
        public loadImpferinnerungen!: (payload?: LoadImpferinnerungenPayload) => void;
        public formatDate!: (input: string | null) => string | null;
        public loadKarteiImpfungen!: (payload: LoadKarteiImpfungenPayload) => void;
        public setBrief!: (impfung: Impfung) => void;
        public setDialogs!: (payload: { brief?: boolean, kartei?: boolean }) => void;
        public printBriefe!: (payload: PrintBriefePayload) => void;
        public sendEmail!: (payload: SendEmailPayload) => void;
        public alsErinnertMarkieren!: (payload: AlsErinnertMarkierenPayload) => void;
        public setSelectedItems!: (items: Impferinnerung[]) => void;
        public sortDate!: CompareFunction<Impferinnerung>;
        public search = '';

        public get selected(): Impferinnerung[] {
            return this.selectedItems;
        }

        public set selected(items: Impferinnerung[]) {
            this.setSelectedItems(items);
        }

        public get briefe(): number[] {
            return this.selected.map((impferinnerung: Impferinnerung) => impferinnerung.patimpfung_id as number);
        }

        public get headers(): DataTableHeaders<Impferinnerung> {
            return [
                { text: 'Nachname', value: 'nachname' },
                { text: 'Vorname', value: 'vorname' },
                { text: 'Titel', value: 'titel' },
                { text: 'Impfung', value: 'textDisplay' },
                { text: 'Impfdatum', value: 'impfdatumDisplay', sort: this.sortDate },
                { text: 'Nächstes Impfdatum', value: 'naechstesimpfdatumDisplay', sort: this.sortDate },
                { text: '', value: 'actions', sortable: false, width: 110 },
            ];
        }

        public mounted(): void {
            this.datumErrorMessages = [];
            this.datumFormatted = this.formatDate(this.filter.faelligBis);
        }

        public onUpdateOptions(pagination?: ImpferinnerungenPagination): void {
            this.loadImpferinnerungen({
                filter: {
                    ...this.filter,
                    pagination: {
                        ...(pagination ?? this.filter.pagination),
                    },
                },
            });
        }

        public onChangeKartei(filter: { satzarten: number[] }): void {
            this.loadKarteiImpfungen({ filter });
        }

        public onCloseBrief(): void {
            this.setBrief(new Impfung());
            this.setDialogs({ brief: false });
        }

        public onChangeFilter(): void {
            this.validateDatum();

            if (this.datumErrorMessages.length === 0) {
                this.onUpdateOptions();
            }
        }

        public onClickPrint(): void {
            const briefe = this.briefe;
            const werden = briefe.length === 1 ? 'wird' : 'werden';
            const e = (array: Array<unknown>): string => array.length === 1 ? '' : 'e';

            confirmSwal(
                'Drucken',
                `Es ${werden} ${briefe.length} Brief${e(briefe)}` +
                    ' gedruckt. Sind Sie sicher?',
                'Drucken',
                'Abbrechen',
            ).then(({ isConfirmed }: SweetAlertResult) => {
                if (isConfirmed) {
                    this.printBriefe({ briefe });
                }
            });
        }

        public onClickEmail(): void {
            const briefe = this.briefe;
            const werden = briefe.length === 1 ? 'wird' : 'werden';
            const s = (array: Array<unknown>): string => array.length === 1 ? '' : 's';

            confirmSwal(
                'E-Mail',
                `Es ${werden} ${briefe.length} E-Mail${s(briefe)}` +
                    ' versendet. Sind Sie sicher?',
                'Senden',
                'Abbrechen',
            ).then(({ isConfirmed }: SweetAlertResult) => {
                if (isConfirmed) {
                    this.sendEmail({ briefe });
                }
            });
        }

        public onClickErinnert(): void {
            const impfungen = this.selected;
            const werden = impfungen.length === 1 ? 'wird' : 'werden';
            const en = (array: Array<unknown>): string => array.length === 1 ? '' : 'en';

            confirmSwal(
                'Erinnert',
                `Es ${werden} ${impfungen.length} Impfung${en(impfungen)} als erinnert` +
                    ' markiert. Sind Sie sicher?',
                'Erinnert',
                'Abbrechen',
            ).then(({ isConfirmed }: SweetAlertResult) => {
                if (isConfirmed) {
                    this.alsErinnertMarkieren({ briefe: this.briefe, filter: this.filter });
                }
            });
        }

        public datumMenu = false;
        public datumFormatted: string | null = null;
        public datumErrorMessages: string[] = [];

        @Watch('filter.faelligBis')
        public onChangeDatum(date: string | null): void {
            this.datumFormatted = this.formatDate(this.filter.faelligBis);

            if (date !== null) {
                this.validateDatum();

                if (this.datumErrorMessages.length === 0) {
                    this.onUpdateOptions();
                }
            }
        }

        public validateDatum(): void {
            this.datumErrorMessages = [];

            for (const rule of [this.rules.validDate]) {
                const valid = rule(this.filter.faelligBis);
                if (valid !== true) {
                    this.datumErrorMessages.push(valid as string);
                }
            }
        }
    }
